// COPIED from ptc-core web/src/api/feedback.ts @ de39fff (fleet feedback contract v1, 2026-10-21).
// Changes from the source: `app: 'crm'` → `'conversions'`; the admin `useFeedbackList` is dropped
// (the admin page lives in the CRM); and core's `useApi()` is replaced by `postFeedback` below, because
// this app's API layer talks to ITS OWN host, while feedback goes to the shared CRM endpoint with the
// CRM's scope (contract §2–§3). `ApiValidationError` is ported from core's `api/client.ts`, same shape.
// Conformance: src/feedback/FeedbackWidgetConformance.test.tsx.
//
// POST /v1/feedback — any staff member. 201 means the report is PERSISTED (status `received`);
// filing it as a GitHub issue is a later, separate step. There is deliberately no reporter, status
// or GitHub field on the create body: the server takes identity from the token.

import { useMutation } from '@tanstack/react-query';
import { getMsalInstance } from '../auth/msalInstance';
import type { FeedbackContext } from './feedbackContext';

/** The shared endpoint (contract §2). Public values; the precondition is the Entra grant (§3). */
export const FEEDBACK_ENDPOINT = 'https://ptc-core-crm-api.azurewebsites.net/api/v1/feedback';
/** The CRM API's delegated scope (contract §3). This app's SPA registration must be granted it. */
export const FEEDBACK_SCOPE = 'api://d80aa38e-6b76-4cf9-96c8-985e718a3a57/access_as_user';

export type FeedbackKind = 'problem' | 'improvement';

export interface CreateFeedbackBody {
  kind: FeedbackKind;
  text: string;
  expected?: string;
  creditByName: boolean;
  app: 'conversions';
  context: FeedbackContext;
}

export interface FeedbackCreated {
  id: string;
  /** `FB-1234` — what the reporter is shown. */
  reference: string;
  status: string;
  createdAt: string;
}

/** Field-level validation errors from a 400 — shape: `{ errors: { field: [msgs] } }` (core's W01). */
export class ApiValidationError extends Error {
  constructor(
    public errors: Record<string, string[] | undefined>,
    public body: string,
  ) {
    super('Validation failed');
    this.name = 'ApiValidationError';
  }

  firstFor(field: string): string | undefined {
    return this.errors[field]?.[0];
  }
}

/**
 * POST the report to the CRM API with a token for the CRM's scope. Throws on anything but 201, so
 * the dialog can never show a reference the server did not issue (contract C9):
 * - 400 with `errors` → `ApiValidationError` (the SSN/EIN refusal lands on its field, C8);
 * - any other failure, including a token the tenant will not issue yet → a plain `Error`.
 */
export async function postFeedback(body: CreateFeedbackBody): Promise<FeedbackCreated> {
  const msal = getMsalInstance();
  const account = msal?.getActiveAccount() ?? msal?.getAllAccounts()[0];
  if (!msal || !account) throw new Error('Not signed in.');
  // No interactive fallback: a consent prompt from a feedback button is a surprise, not a fix.
  // Until the grant exists this throws, and the dialog shows its send-failed line.
  const { accessToken } = await msal.acquireTokenSilent({ scopes: [FEEDBACK_SCOPE], account });

  const res = await fetch(FEEDBACK_ENDPOINT, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${accessToken}` },
    body: JSON.stringify(body),
  });
  const text = await res.text();
  if (res.status === 201) return JSON.parse(text) as FeedbackCreated;
  if (res.status === 400) {
    try {
      const parsed = JSON.parse(text) as { errors?: Record<string, string[]> };
      if (parsed?.errors) throw new ApiValidationError(parsed.errors, text);
    } catch (e) {
      if (e instanceof ApiValidationError) throw e;
    }
  }
  throw new Error(`Feedback was not accepted (${res.status}).`);
}

export function useCreateFeedback() {
  return useMutation({ mutationFn: postFeedback });
}
